import { useQuery } from '@apollo/client';
import { Form, Input, message, Modal, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { isTrackingNumber } from '../../../../utils/BarCodeUtil';
import {
    GET_AVAILABLE_CONSOLIDATION_TASKS,
    GetAvailableConsolidationTasksResponse,
    GetAvailableConsolidationTasksVariables
} from '../graphql/queries/getAvailableConsolidationTasks';
import type { ConsolidationTaskOption } from '../graphql/types/ConsolidationTypes';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';

interface MoveToConsolidationModalProps {
  visible: boolean;
  defaultTaskId: string;
  disabled?: boolean;
  type: number; // 1: SWIFTX, 2: Other Carrier
  onOk: (taskId: string, trackingNumbers: string[]) => void;
  onCancel: () => void;
}

const { TextArea } = Input;

const MoveToConsolidationModal: React.FC<MoveToConsolidationModalProps> = ({
  visible,
  defaultTaskId,
  disabled = false,
  type,
  onOk,
  onCancel
}) => {
  const [taskId, setTaskId] = useState<string>(defaultTaskId);
  const [trackingInput, setTrackingInput] = useState<string>('');
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouseContext();

  const { data, loading } = useQuery<
    GetAvailableConsolidationTasksResponse,
    GetAvailableConsolidationTasksVariables
  >(GET_AVAILABLE_CONSOLIDATION_TASKS, {
    variables: {
      warehouseId: currentWarehouse?.id!,
      type: type
    },
    skip: !currentWarehouse?.id || !visible,
    fetchPolicy: 'network-only'
  });

  const options: ConsolidationTaskOption[] = data?.getAvailableConsolidationTasks || [];

  useEffect(() => {
    setTaskId(defaultTaskId);
    setTrackingInput('');
  }, [visible, defaultTaskId]);

  const handleOk = () => {
    if (disabled) return; // 防止重复点击
    if (!taskId){
      message.error(t('wms.baggingTask.messages.pleaseSelectTheTargetBaggingTask'))
      return
    }
    // 支持逗号、分号、空白符及回车换行分割运单号
    const trackingNumbers = trackingInput.split(/[\n\r\s,;]+/).filter(Boolean);
    if (trackingNumbers.length === 0){
      message.error(t('wms.baggingTask.messages.pleaseEnterTheTrackingNumber'))
      return
    }
    const invalid = trackingNumbers.find(tn => !isTrackingNumber(tn));
    if (invalid) {
      message.error(`${invalid} ${t('wms.baggingTask.messages.isWrong,PleaseCheckFormat')}`);
      return;
    }
    onOk(taskId, trackingNumbers);
  };

  return (
    <Modal
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText={t('components.button.ok')}
      cancelText={t('components.button.cancel')}
      width={600}
      destroyOnClose
      okButtonProps={{ disabled }}
    >
      <Form layout="vertical">
        <Form.Item label={t('wms.baggingTask.filters.baggingTask')}>
          <Select value={taskId} onChange={setTaskId} loading={loading}>
            {options.map(opt => (
              <Select.Option key={opt.taskId} value={opt.taskId}>
                {opt.displayName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label={t('wms.baggingTask.filters.trk')}>
          <TextArea
            rows={4}
            placeholder={t('wms.baggingTask.placeholders.trk') || ''}
            value={trackingInput}
            onChange={e => setTrackingInput(e.target.value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MoveToConsolidationModal;